/**
 * New / edit Misc Income receipt or Misc Expense payment.
 * Student search looks in ALL sessions (e.g. last year's XII student paying TC fee).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Form, Row, Col, InputGroup, ListGroup, Badge, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import schoolApi from "../../api/schoolApi";
import { TYPE_INFO, PAYMENT_METHODS, today, num } from "./ledgerUtils";

const blockMinus = (e) => ["-", "e", "E", "+"].includes(e.key) && e.preventDefault();

const emptyForm = (type, sessionId) => ({
  entry_type: type,
  category_id: "",
  session_id: sessionId || "",
  entry_date: today(),
  amount: "",
  payment_method: "Cash",
  transaction_no: "",
  party_type: type === "INCOME" ? "STUDENT" : "VENDOR",
  student_id: "",
  student_addmission_id: "",
  party_name: "",
  party_phone: "",
  remark: "",
});

const LedgerEntryModal = ({ show, onHide, type, sessionId, categories, entry, onSaved }) => {
  const info = TYPE_INFO[type];
  const [form, setForm] = useState(emptyForm(type, sessionId));
  const [student, setStudent] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);

  const cats = useMemo(
    () => (categories || []).filter((c) => c.type === type && (c.status === "active" || c.id === entry?.category_id)),
    [categories, type, entry]
  );
  const cat = cats.find((c) => c.id === form.category_id);

  useEffect(() => {
    if (!show) return;
    if (entry) {
      setForm({
        ...emptyForm(type, sessionId),
        ...entry,
        entry_date: String(entry.entry_date || "").slice(0, 10),
        amount: String(num(entry.amount)),
        transaction_no: entry.transaction_no || "",
        party_name: entry.party_name || "",
        party_phone: entry.party_phone || "",
        remark: entry.remark || "",
        student_id: entry.student_id || "",
        student_addmission_id: entry.student_addmission_id || "",
      });
      setStudent(
        entry.student_id
          ? { student_id: entry.student_id, student_addmission_id: entry.student_addmission_id, student_name: entry.student_name,
              classname: entry.classname, sectionname: entry.sectionname, session_year: entry.admission_session }
          : null
      );
    } else {
      setForm(emptyForm(type, sessionId));
      setStudent(null);
    }
    setQ("");
    setResults([]);
  }, [show, entry, type, sessionId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickCategory = (id) => {
    const c = cats.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      category_id: id,
      amount: !f.amount && c?.default_amount ? String(num(c.default_amount)) : f.amount,
      party_type: c?.needs_student ? "STUDENT" : f.party_type,
    }));
  };

  const search = (text) => {
    setQ(text);
    clearTimeout(timer.current);
    if (String(text).trim().length < 2) return setResults([]);
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await schoolApi.ledgerGet("/students/search", { q: text.trim() });
        setResults(r?.result || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
  };

  const pickStudent = (s) => {
    setStudent(s);
    setForm((f) => ({
      ...f,
      student_id: s.student_id,
      student_addmission_id: s.student_addmission_id || "",
      party_phone: f.party_phone || s.phone || "",
    }));
    setQ("");
    setResults([]);
  };

  const save = async () => {
    if (!form.category_id) return toast.warn("Select a category");
    if (!form.session_id) return toast.warn("Select a session from the header first");
    if (!form.entry_date) return toast.warn("Select the date");
    if (!(num(form.amount) > 0)) return toast.warn("Enter amount more than 0");
    if (form.party_type === "STUDENT" && !form.student_id) return toast.warn("Search and select the student");
    if (form.party_type !== "STUDENT" && !form.party_name.trim()) return toast.warn(`${info.partyLabel} name is required`);
    if (form.payment_method !== "Cash" && !form.transaction_no.trim())
      return toast.warn("Enter transaction / cheque number");
    setSaving(true);
    try {
      const body = { ...form, amount: num(form.amount) };
      const res = entry
        ? await schoolApi.ledgerPut(`/entries/${entry.id}`, body)
        : await schoolApi.ledgerPost("/entries", body);
      if (res?.success) {
        toast.success(entry ? "Entry updated" : `${type === "INCOME" ? "Receipt" : "Payment"} ${res.result?.receipt_number} saved`);
        onSaved && onSaved(res.result, !entry);
        onHide();
      } else toast.error(res?.message || "Could not save");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton style={{ borderTop: `4px solid ${info.color}` }}>
        <Modal.Title style={{ fontSize: 18 }}>
          {entry ? `Edit ${entry.receipt_number}` : info.newLabel} – {info.title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          <Col md={6}>
            <Form.Label className="small fw-semibold">Category *</Form.Label>
            <Form.Select value={form.category_id} onChange={(e) => pickCategory(e.target.value)}>
              <option value="">-- Select --</option>
              {cats.map((c) => (
                <option key={c.id} value={c.id}>{c.name}{c.needs_student ? " (student)" : ""}</option>
              ))}
            </Form.Select>
            {!cats.length && <small className="text-danger">No active category. Add one in Ledger Categories.</small>}
          </Col>
          <Col md={3}>
            <Form.Label className="small fw-semibold">Date *</Form.Label>
            <Form.Control type="date" value={form.entry_date} max={today()} onChange={(e) => set("entry_date", e.target.value)} />
          </Col>
          <Col md={3}>
            <Form.Label className="small fw-semibold">Amount (₹) *</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={form.amount}
              onKeyDown={blockMinus}
              onChange={(e) => set("amount", e.target.value)}
              placeholder="0"
            />
          </Col>

          <Col md={12}>
            <Form.Label className="small fw-semibold d-block">{info.partyLabel} *</Form.Label>
            <div className="d-flex flex-wrap gap-3 mb-2">
              {(type === "INCOME" ? ["STUDENT", "OTHER"] : ["VENDOR", "STAFF", "STUDENT", "OTHER"]).map((pt) => (
                <Form.Check
                  key={pt}
                  inline
                  type="radio"
                  id={`pt-${pt}`}
                  label={pt.charAt(0) + pt.slice(1).toLowerCase()}
                  checked={form.party_type === pt}
                  disabled={cat?.needs_student && pt !== "STUDENT"}
                  onChange={() => set("party_type", pt)}
                />
              ))}
            </div>
            {form.party_type === "STUDENT" ? (
              student ? (
                <div className="d-flex align-items-center justify-content-between border rounded px-3 py-2 bg-light">
                  <div>
                    <strong>{student.student_name}</strong>{" "}
                    <span className="text-muted small">
                      {student.classname ? `Class ${student.classname}${student.sectionname ? " - " + student.sectionname : ""}` : ""}
                      {student.session_year ? ` · ${student.session_year}` : ""}
                    </span>
                  </div>
                  <Button size="sm" variant="link" onClick={() => { setStudent(null); set("student_id", ""); set("student_addmission_id", ""); }}>
                    Change
                  </Button>
                </div>
              ) : (
                <div className="position-relative">
                  <InputGroup>
                    <InputGroup.Text><i className="fa-solid fa-magnifying-glass" /></InputGroup.Text>
                    <Form.Control
                      placeholder="Search student by name or phone (all sessions)"
                      value={q}
                      onChange={(e) => search(e.target.value)}
                      autoFocus
                    />
                    {searching && <InputGroup.Text><Spinner size="sm" animation="border" /></InputGroup.Text>}
                  </InputGroup>
                  {results.length > 0 && (
                    <ListGroup className="position-absolute w-100 shadow-sm" style={{ zIndex: 20, maxHeight: 260, overflowY: "auto" }}>
                      {results.map((s) => (
                        <ListGroup.Item action key={s.student_id} onClick={() => pickStudent(s)}>
                          <div className="d-flex justify-content-between">
                            <span><strong>{s.student_name}</strong> {s.phone ? <small className="text-muted">· {s.phone}</small> : null}</span>
                            <span className="small">
                              {s.classname ? `Class ${s.classname}${s.sectionname ? " - " + s.sectionname : ""}` : ""}{" "}
                              {s.session_year && <Badge bg="secondary">{s.session_year}</Badge>}
                            </span>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                  {q.trim().length >= 2 && !searching && !results.length && (
                    <small className="text-muted">No student found</small>
                  )}
                </div>
              )
            ) : (
              <Row className="g-2">
                <Col md={8}>
                  <Form.Control
                    maxLength={150}
                    placeholder={form.party_type === "STAFF" ? "Staff name" : form.party_type === "VENDOR" ? "Vendor / shop name" : "Name"}
                    value={form.party_name}
                    onChange={(e) => set("party_name", e.target.value)}
                  />
                </Col>
                <Col md={4}>
                  <Form.Control
                    maxLength={20}
                    placeholder="Phone"
                    value={form.party_phone}
                    onChange={(e) => set("party_phone", e.target.value.replace(/[^\d+ ]/g, ""))}
                  />
                </Col>
              </Row>
            )}
          </Col>

          <Col md={4}>
            <Form.Label className="small fw-semibold">Payment Mode</Form.Label>
            <Form.Select value={form.payment_method} onChange={(e) => set("payment_method", e.target.value)}>
              {PAYMENT_METHODS.map((m) => <option key={m}>{m}</option>)}
            </Form.Select>
          </Col>
          <Col md={8}>
            <Form.Label className="small fw-semibold">
              Transaction / Cheque No.{form.payment_method !== "Cash" ? " *" : ""}
            </Form.Label>
            <Form.Control
              maxLength={100}
              value={form.transaction_no}
              disabled={form.payment_method === "Cash"}
              onChange={(e) => set("transaction_no", e.target.value)}
            />
          </Col>
          <Col md={12}>
            <Form.Label className="small fw-semibold">Remark</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              maxLength={2550}
              value={form.remark}
              onChange={(e) => set("remark", e.target.value)}
              placeholder={type === "INCOME" ? "e.g. TC fee for session 2025-26" : "e.g. Bill no. 123 – electricity for September"}
            />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onHide}>Cancel</Button>
        <Button style={{ background: info.color, borderColor: info.color }} onClick={save} disabled={saving}>
          {saving ? "Saving..." : entry ? "Update" : "Save & Print"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LedgerEntryModal;
